/**
 * Super-admin payment management — Razorpay subscription payments, webhooks, refunds.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import swal from 'sweetalert';
import Sidebar_Progress from '../../../layouts/Admin_Sidebar_Progress.component';
import LoginHeaderOperational from '../../LoginHeaderOperational';
import { API_URL } from '../../../../constant/API_Settings';
import { fetchPlatformSuperAdminProfile } from '../../../../utils/platformAuth';
import {
  adminCheckPaymentStatus,
  fetchAdminPaymentDetail,
  fetchAdminPayments,
} from '../adminApi';
import '../admin-dashboard.css';
import './AdminPaymentManagement.css';

const STATUS_FILTERS = [
  { id: '', label: 'All statuses' },
  { id: '0', label: 'Initiated' },
  { id: '1', label: 'Success' },
  { id: '2', label: 'Failure' },
  { id: '3', label: 'Aborted' },
  { id: '5', label: 'Error' },
];

const GATEWAY_FILTERS = [
  { id: '', label: 'All gateways' },
  { id: 'razorpay', label: 'Razorpay' },
  { id: 'payu', label: 'PayU' },
  { id: 'ccavenue', label: 'CCAvenue' },
];

function formatInr(value) {
  return `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function statusTone(status) {
  if (status === 1) return 'success';
  if (status === 0) return 'pending';
  return 'danger';
}

const AdminPaymentManagement = (props) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0, per_page: 50 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [checkingId, setCheckingId] = useState(null);
  const skipSearchDebounce = useRef(true);

  const load = useCallback((page = 1, query = search, status = statusFilter, gateway = gatewayFilter) => {
    setLoading(true);
    fetchAdminPayments({ search: query, status, gateway, page, perPage: 50 })
      .then((res) => {
        setRows(res.data || []);
        setStats(res.stats || {});
        setPagination(res.pagination || {});
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setLoading(false));
  }, [search, statusFilter, gatewayFilter]);

  useEffect(() => {
    fetchPlatformSuperAdminProfile(API_URL, localStorage.getItem('AUTH_TOKEN'))
      .then((profile) => {
        if (!profile) {
          history.goBack();
          return;
        }
        load(1, '', '', '');
      })
      .catch(() => history.goBack());
  }, [history, load]);

  useEffect(() => {
    if (skipSearchDebounce.current) {
      skipSearchDebounce.current = false;
      return undefined;
    }
    const timer = setTimeout(() => load(1, search, statusFilter, gatewayFilter), 350);
    return () => clearTimeout(timer);
  }, [search, statusFilter, gatewayFilter, load]);

  function openDetail(paymentId) {
    setDetailLoading(true);
    setDetail(null);
    fetchAdminPaymentDetail(paymentId)
      .then((res) => setDetail(res))
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setDetailLoading(false));
  }

  function handleCheckStatus(paymentId) {
    setCheckingId(paymentId);
    adminCheckPaymentStatus(paymentId)
      .then((res) => {
        swal('Status synced', res.message || 'Payment status updated from Razorpay.', 'success');
        load(pagination.current_page);
        if (detail?.payment?.id === paymentId) {
          openDetail(paymentId);
        }
      })
      .catch((err) => swal('Error', err.message, 'error'))
      .finally(() => setCheckingId(null));
  }

  const kpiCards = [
    { label: 'Total payments', value: stats.total || 0, tone: 'default' },
    { label: 'Successful', value: stats.success || 0, tone: 'success' },
    { label: 'Pending', value: stats.pending || 0, tone: 'warning' },
    { label: 'Failed', value: stats.failed || 0, tone: 'danger' },
    { label: 'Collected', value: formatInr(stats.total_collected), tone: 'success' },
    { label: 'Refunded', value: formatInr(stats.total_refunded), tone: 'warning' },
  ];

  return (
    <div className="main-container page-wrap admin-platform admin-payment-page">
      <div className="d-md-flex">
        <Sidebar_Progress props={props} progress={32} />
        <div className="content-area">
          <LoginHeaderOperational props={props} page_title="Payment Management" />
          <div className="container pb-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 mb-2">
              <div>
                <Link to="/admin_dashboard" className="small">← Control Center</Link>
                <p className="text-muted small mb-0 mt-1">
                  View all subscription gateway payments, check Razorpay status, and inspect invoices and receipts.
                </p>
              </div>
            </div>

            <div className="admin-pay-kpi-grid">
              {kpiCards.map((card) => (
                <div key={card.label} className={`admin-pay-kpi admin-pay-kpi--${card.tone}`}>
                  <p className="admin-pay-kpi__value">{card.value}</p>
                  <p className="admin-pay-kpi__label">{card.label}</p>
                </div>
              ))}
            </div>

            <div className="admin-panel admin-pay-webhook-info">
              <h6 className="mb-1">Razorpay webhook URL</h6>
              <p className="small text-muted mb-1">
                Register this URL in Razorpay Dashboard → Settings → Webhooks. Enable:
                {' '}<code>payment.captured</code>, <code>payment.failed</code>, <code>order.paid</code>, <code>refund.created</code>, <code>refund.processed</code>
              </p>
              <code className="admin-pay-webhook-url">{`${API_URL.replace('/api', '')}/api/razorpay/webhook`}</code>
            </div>

            <div className="admin-panel">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <div>
                  <h5 className="mb-0">Gateway payments</h5>
                  <p className="small text-muted mb-0">
                    {pagination.total ? `${pagination.total} payment(s) total` : 'Subscription payments'}
                  </p>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <select
                    className="form-select form-select-sm"
                    value={gatewayFilter}
                    onChange={(e) => setGatewayFilter(e.target.value)}
                  >
                    {GATEWAY_FILTERS.map((f) => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {STATUS_FILTERS.map((f) => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    style={{ maxWidth: 280 }}
                    placeholder="Search order, pump, email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>

              {loading ? (
                <p className="text-muted">Loading payments…</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover admin-pay-table">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Pump</th>
                        <th>Plan</th>
                        <th>Amount</th>
                        <th>Gateway</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={8} className="text-muted">No payments found.</td></tr>
                      ) : rows.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <span className="admin-pay-order">{row.order_id}</span>
                            {row.razorpay_payment_id && (
                              <div className="small text-muted">{row.razorpay_payment_id}</div>
                            )}
                          </td>
                          <td>{row.pump_name}</td>
                          <td>{row.plan_name}</td>
                          <td>{formatInr(row.amount)}</td>
                          <td>
                            <span className="admin-pay-gateway">{row.payment_gateway}</span>
                            {row.payment_method && (
                              <div className="small text-muted">{row.payment_method}</div>
                            )}
                          </td>
                          <td>
                            <span className={`admin-pay-status admin-pay-status--${statusTone(row.payment_status)}`}>
                              {row.payment_status_label}
                            </span>
                          </td>
                          <td className="small">{row.created_at}</td>
                          <td>
                            <div className="admin-action-row">
                              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => openDetail(row.id)}>
                                Details
                              </button>
                              {row.payment_gateway === 'razorpay' && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  disabled={checkingId === row.id}
                                  onClick={() => handleCheckStatus(row.id)}
                                >
                                  {checkingId === row.id ? 'Checking…' : 'Check status'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {pagination.last_page > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <span className="small text-muted">Page {pagination.current_page} of {pagination.last_page}</span>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={pagination.current_page <= 1}
                      onClick={() => load(pagination.current_page - 1)}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      disabled={pagination.current_page >= pagination.last_page}
                      onClick={() => load(pagination.current_page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {(detail || detailLoading) && (
        <div className="admin-pay-modal-backdrop" onClick={() => setDetail(null)} role="presentation">
          <div className="admin-pay-modal" onClick={(e) => e.stopPropagation()} role="dialog">
            <div className="d-flex justify-content-between align-items-start mb-3">
              <h5 className="mb-0">Payment details</h5>
              <button type="button" className="btn-close" onClick={() => setDetail(null)} aria-label="Close" />
            </div>

            {detailLoading ? (
              <p className="text-muted">Loading…</p>
            ) : detail?.payment ? (
              <>
                <div className="admin-pay-detail-grid">
                  <div><span className="text-muted">Order ID</span><br />{detail.payment.order_id}</div>
                  <div><span className="text-muted">Pump</span><br />{detail.payment.pump_name}</div>
                  <div><span className="text-muted">Plan</span><br />{detail.payment.plan_name}</div>
                  <div><span className="text-muted">Amount</span><br />{formatInr(detail.payment.amount)}</div>
                  <div><span className="text-muted">Status</span><br />{detail.payment.payment_status_label}</div>
                  <div><span className="text-muted">Gateway</span><br />{detail.payment.payment_gateway}</div>
                  <div><span className="text-muted">Razorpay Order</span><br />{detail.payment.razorpay_order_id || '—'}</div>
                  <div><span className="text-muted">Razorpay Payment</span><br />{detail.payment.razorpay_payment_id || '—'}</div>
                  <div><span className="text-muted">Billing</span><br />{detail.payment.billing_name}<br /><span className="small text-muted">{detail.payment.billing_email}</span></div>
                  <div><span className="text-muted">Paid at</span><br />{detail.payment.paid_at || '—'}</div>
                </div>

                {detail.subscription && (
                  <div className="admin-pay-section">
                    <h6>Subscription</h6>
                    <p className="small mb-0">
                      {detail.subscription.plan_name} · {detail.subscription.start_date} → {detail.subscription.end_date}
                    </p>
                  </div>
                )}

                {detail.invoice && (
                  <div className="admin-pay-section">
                    <h6>Invoice</h6>
                    <p className="small mb-0">
                      #{detail.invoice.id} · {formatInr(detail.invoice.total_amount)}
                      {detail.invoice.is_draft ? ' (draft)' : ' (approved)'}
                      · Received {formatInr(detail.invoice.amount_received)}
                    </p>
                  </div>
                )}

                {detail.receipts?.length > 0 && (
                  <div className="admin-pay-section">
                    <h6>Receipts ({detail.receipts.length})</h6>
                    <ul className="small mb-0 ps-3">
                      {detail.receipts.map((r) => (
                        <li key={r.id}>
                          {r.receipt_date} · {formatInr(r.amount)} · {r.payment_mode}
                          {r.reference_no ? ` · Ref ${r.reference_no}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {detail.refunds?.length > 0 && (
                  <div className="admin-pay-section">
                    <h6>Refunds</h6>
                    <ul className="small mb-0 ps-3">
                      {detail.refunds.map((r) => (
                        <li key={r.id}>{r.refund_id} · {formatInr(r.amount)} · {r.status}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {detail.gateway_events?.length > 0 && (
                  <div className="admin-pay-section">
                    <h6>Webhook events ({detail.gateway_events.length})</h6>
                    <ul className="small mb-0 ps-3">
                      {detail.gateway_events.map((e) => (
                        <li key={e.id}>
                          {e.event_type} · {e.processing_status}
                          {e.signature_verified ? '' : ' (sig failed)'}
                          · {e.created_at}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {detail.payment.payment_gateway === 'razorpay' && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary mt-2"
                    disabled={checkingId === detail.payment.id}
                    onClick={() => handleCheckStatus(detail.payment.id)}
                  >
                    {checkingId === detail.payment.id ? 'Checking…' : 'Sync status from Razorpay'}
                  </button>
                )}
              </>
            ) : (
              <p className="text-muted">Payment not found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentManagement;
